/**
 * Named counts of best-effort failures, beside the `console.error` that reports each one.
 *
 * A fire-and-forget domain action (a save, a prefetch, a telemetry beacon, a trigger attach)
 * whose promise rejects is a lost effect, never a clean no-op (P-08): `reportFailure` logs it
 * under a stable `<area>.<verb>_failed` name — services/consoleCapture.js carries the line
 * into the telemetry diagnostics group — and counts it here for the session. A deliberate
 * swallow (a cancel, an abort, a flush) carries `// swallow-ok: cleanup` instead; the
 * `unannotatedSwallows` ratchet (scripts/ratchet-server.mjs) keeps the two from blurring.
 */
const _counts = new Map();

/**
 * Log `err` under `name` and count it: the one form a failed fire-and-forget takes.
 * @param {string} name
 * @param {unknown} err
 */
export function reportFailure(name, err) {
  console.error(name, err);
  increment(name);
}

/**
 * Count one failure under `name` without logging (the log already happened elsewhere).
 * @param {string} name
 */
export function increment(name) {
  _counts.set(name, (_counts.get(name) || 0) + 1);
}

/** `{ [name]: count }` of every failure counted since the last `reset()`. */
export function snapshot() {
  return Object.fromEntries(_counts);
}

/** Forget every count (tests; a cleared bug-report buffer). */
export function reset() {
  _counts.clear();
}
